import { useRef, useState } from "react";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import useAuthStatus from "../../hooks/useAuthStatus";
import { useAuth } from "../../context/useAuth";
import ContactScreen from "./ContactScreen";
import { useNavigate } from "react-router-dom";

const faqs = [
  {
    category: "Buying Crypto",
    question: "How do I buy crypto with Naira?",
    answer:
      "Go to the Buy page, select your preferred crypto asset, enter the Naira amount you want to spend, then complete payment via Paystack. Your crypto will be credited within minutes after payment confirmation.",
  },
  {
    category: "Buying Crypto",
    question: "What is the minimum buy amount?",
    answer:
      "The minimum purchase amount is ₦1,000. There is no maximum limit, but transactions above ₦5,000,000 may require additional verification.",
  },
  {
    category: "Selling Crypto",
    question: "How long does a Naira withdrawal take?",
    answer:
      "Once you confirm a sell order, the Naira equivalent is sent to your linked bank account within 5–30 minutes on business days. Bank processing times may vary.",
  },
  {
    category: "Deposits & Withdrawals",
    question: "How do I deposit crypto into my wallet?",
    answer:
      "Navigate to the Deposit page, select the crypto asset, copy your unique wallet address or scan the QR code, then send from your external wallet. Deposits are credited after the required network confirmations.",
  },
  {
    category: "Deposits & Withdrawals",
    question: "How do I withdraw crypto to an external wallet?",
    answer:
      "Go to Withdraw Crypto, select the asset, enter the destination wallet address and amount, then confirm. Withdrawals are processed within 10–30 minutes depending on network congestion.",
  },
  {
    category: "Account & KYC",
    question: "Why do I need to complete KYC?",
    answer:
      "KYC (Know Your Customer) is required by Nigerian financial regulations. It helps us protect your account, prevent fraud, and comply with CBN and SEC guidelines.",
  },
  {
    category: "Account & KYC",
    question: "My KYC is pending for too long. What do I do?",
    answer:
      "KYC verification typically takes 1–24 hours. If it has been longer than 24 hours, please contact our support team with your registered email and we will resolve it promptly.",
  },
  {
    category: "Fees",
    question: "What fees does NairaX charge?",
    answer:
      "NairaX charges a 1.5% fee on buy/sell transactions and a flat ₦50 fee on Naira withdrawals. Crypto withdrawal fees vary by network (e.g., BTC, ETH gas fees).",
  },
];

const categories = Array.from(new Set(faqs.map((f) => f.category)));

const quickLinks = [
  { icon: "ri-whatsapp-line", label: "WhatsApp Support", sub: "Chat instantly", color: "#25D366", href: "https://wa.me/2348012345678" },
  { icon: "ri-mail-send-line", label: "Email Support", sub: "support@nairax.ng", color: "#059669", href: "mailto:support@nairax.ng" },
  { icon: "ri-twitter-x-line", label: "Twitter / X", sub: "@NairaX_official", color: "#0F172A", href: "https://twitter.com/NairaXofficial" },
];

export default function SupportPage() {
  const { user } = useAuth();
  const navigate = useNavigate()
  
  const [activeTab, setActiveTab] = useState("help");
  const [openFaq, setOpenFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Buying Crypto");

const filteredFaqs = faqs.filter((f) => f.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28">


        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            
            <button onClick={() =>  navigate("/home") }
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
            >
              <i className="ri-arrow-left-line text-slate-600 text-lg" />
            </button>
            <div>
              <h2 className="text-slate-800 font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Help &amp; Support
              </h2>
              <p className="text-slate-400 text-xs">We&apos;re here to help you</p>
            </div>
          </div>
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
            <i className="ri-customer-service-2-line text-amber-500 text-lg" />
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="px-5 pt-4 pb-3 flex-shrink-0">
          <div className="flex bg-slate-100 rounded-full p-1 gap-1">
            {(["help", "contact"]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab ? "bg-white text-slate-800 shadow-sm" : "text-slate-400"
                }`}
              >
                {tab === "help" ? "FAQ" : "Contact Us"}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 pb-8">
          {/* ── FAQ Tab ── */}
          {activeTab === "help" && (
            <div>
              {/* Quick contact links */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {quickLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 bg-slate-50 border border-slate-100 rounded-2xl py-4 px-2 hover:bg-slate-100 transition-colors cursor-pointer"
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${link.color}15` }}
                    >
                      <i className={`${link.icon} text-lg`} style={{ color: link.color }} />
                    </div>
                    <p className="text-slate-700 text-xs font-semibold text-center leading-tight">{link.label}</p>
                  </a>
                ))}
              </div>

              {/* Category tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { setActiveCategory(cat); setOpenFaq(null); }}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      activeCategory === cat
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* FAQ accordion */}
              <div className="divide-y divide-slate-100 bg-slate-50 rounded-2xl overflow-hidden">
                {filteredFaqs.map((faq, idx) => (
                  <div key={idx}>
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-start gap-3 px-4 py-4 text-left cursor-pointer hover:bg-slate-100 transition-colors"
                    >
                      <div className="w-6 h-6 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-question-line text-emerald-600 text-xs" />
                      </div>
                      <p className="flex-1 text-slate-700 text-sm font-medium leading-snug">{faq.question}</p>
                      <i
                        className={`ri-arrow-down-s-line text-slate-300 text-lg flex-shrink-0 transition-transform mt-0.5 ${
                          openFaq === idx ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {openFaq === idx && (
                      <div className="px-4 pb-4 pt-0">
                        <div className="ml-9 bg-white border border-slate-100 rounded-xl px-4 py-3">
                          <p className="text-slate-500 text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-slate-400 text-xs text-center mt-5">
                Can&apos;t find your answer?{" "}
                <button
                  onClick={() => setActiveTab("contact")}
                  className="text-emerald-600 font-semibold cursor-pointer hover:underline"
                >
                  Contact us directly
                </button>
              </p>
            </div>
          )}

          {/* ── Contact Tab ── */}
          {activeTab === "contact" && (
         <ContactScreen user={user} />
        )}
        </div>

        <MobileBottomNav />
     
    </div>
  );
}
